import React, {
  CSSProperties,
  PropsWithChildren,
  useEffect,
  useRef
} from 'react';
import { createPortal } from 'react-dom';

import UIConfig from '@/UIConfig.ts';

interface ModalProps extends PropsWithChildren {
  handleClose?: () => void;
  open: boolean;
  fullScreen?: boolean;
  scrollable?: boolean;
}

const Backdrop: React.FC<
  PropsWithChildren & {
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    fullscreen?: boolean;
  }
> = ({ children, onClick, fullscreen }) => {
  return (
    <div
      onClick={onClick}
      className="backdrop"
      style={{
        ...backdropStyles,
        top: fullscreen ? 100 : backdropStyles.top,
        backgroundColor: fullscreen
          ? UIConfig.Colors.BackgroundDark
          : 'rgb(0 0 0 / 60%)'
      }}
    >
      {children}
    </div>
  );
};

const Modal: React.FC<ModalProps> = ({
  children,
  open,
  handleClose,
  fullScreen,
  scrollable
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current.style.transition = 'none';
      modalRef.current.style.opacity = '0';

      if (!fullScreen) {
        modalRef.current.style.transform = 'translateY(100%)';
      } else {
        modalRef.current.style.transform = 'none';
      }

      modalRef.current.offsetHeight;

      modalRef.current.style.transition = fullScreen
        ? 'opacity 0.2s ease'
        : 'transform 0.15s ease, opacity 0.1s ease';
      modalRef.current.style.opacity = '1';
      if (!fullScreen) {
        modalRef.current.style.transform = 'translateY(0)';
      }
    }
  }, [open, fullScreen]);

  if (!open) return null;

  return createPortal(
    <Backdrop onClick={handleClose} fullscreen={fullScreen}>
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        className="modal orange-gradient"
        style={{
          ...modalStyle,
          height: fullScreen ? '100%' : modalStyle.height,
          transform: fullScreen ? 'none' : modalStyle.transform,
          overflowY: scrollable ? 'auto' : undefined,
          WebkitOverflowScrolling: scrollable ? 'touch' : undefined
        }}
      >
        {children}
      </div>
    </Backdrop>,
    document.body
  );
};

const backdropStyles: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgb(0 0 0 / 60%)',
  zIndex: 999,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-end',
  flexDirection: 'column'
};

const modalStyle: CSSProperties = {
  position: 'absolute',
  width: '100%',
  minHeight: '30%',
  zIndex: 999999,
  padding: '0px',
  opacity: 0, // Initial state for animation
  transform: 'translateY(100%)' // Start off-screen for non-fullscreen
};

export default Modal;
